import { FREDRICK_DERIVED_SCHEMA_VERSION, FREDRICK_OWL_PAGE_REVISION_TYPE, expectedOwlPageCount, expectedOwlRowsOnPage, } from 'fredrick-data-contract';
import { createFredrickContinuationSearchClaimV1, createFredrickRootSearchClaimV1, fredrickSearchClaimIsCanonical, } from 'fredrick-data-contract/claims';
import { derivedSearchIsStructurallyComplete, } from 'fredrick-data-contract/derived';
import { mapleLegendsMarketSemantics, } from './semantics.js';
export { FREDRICK_MARKET_VARIANT_MAPLELEGENDS_V1, mapleLegendsMarketSemantics, } from './semantics.js';
export const FREDRICK_MARKET_PROCESSOR_VERSION = 'fredrick-market-processor-v5';
export const FREDRICK_MARKET_STATISTICS_VERSION = 'fredrick-market-statistics-v2';
function compareText(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
function compareNumber(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
function sortedStrings(values) {
    return [...values].sort(compareText);
}
function compareCanonicalPageRecord(left, right) {
    return compareNumber(left.revision.capture.importedAt, right.revision.capture.importedAt)
        || compareNumber(left.revision.capture.observedAt, right.revision.capture.observedAt)
        || compareText(left.sourceImageId, right.sourceImageId)
        || compareText(left.revision.revisionId, right.revision.revisionId);
}
function canonicalRevisionOrder(input) {
    const revision = input.revision;
    return [
        revision.recordType === FREDRICK_OWL_PAGE_REVISION_TYPE ? 0 : 1,
        revision.capture.importedAt,
        revision.capture.observedAt,
        revision.revisionId,
    ];
}
function compareTuple(left, right) {
    for (let index = 0; index < Math.min(left.length, right.length); index += 1) {
        const a = left[index];
        const b = right[index];
        const result = typeof a === 'number' && typeof b === 'number'
            ? compareNumber(a, b)
            : compareText(String(a), String(b));
        if (result !== 0)
            return result;
    }
    return compareNumber(left.length, right.length);
}
function deduplicateRevisionInputs(inputs) {
    const groups = new Map();
    for (const input of inputs) {
        const values = groups.get(input.revision.revisionId);
        if (values === undefined)
            groups.set(input.revision.revisionId, [input]);
        else
            values.push(input);
    }
    return [...groups.values()]
        .map((values) => [...values].sort((a, b) => compareTuple(canonicalRevisionOrder(a), canonicalRevisionOrder(b)))[0])
        .sort((a, b) => compareText(a.revision.revisionId, b.revision.revisionId));
}
function selectPreferredInputs(inputs) {
    const deduplicated = deduplicateRevisionInputs(inputs);
    const sourceByRevisionId = new Map();
    for (const input of deduplicated) {
        sourceByRevisionId.set(input.revision.revisionId, input.revision.capture.sourceImage.sourceImageId);
    }
    const bySource = new Map();
    for (const input of deduplicated) {
        const sourceImageId = input.revision.capture.sourceImage.sourceImageId;
        const values = bySource.get(sourceImageId);
        if (values === undefined)
            bySource.set(sourceImageId, [input]);
        else
            values.push(input);
    }
    const selected = [];
    const unassigned = new Set();
    for (const sourceInputs of bySource.values()) {
        const ids = new Set(sourceInputs.map((input) => input.revision.revisionId));
        const childCount = new Map();
        const parentById = new Map();
        let invalid = false;
        for (const input of sourceInputs) {
            const parent = input.revision.supersedesRevisionId;
            if (parent === undefined)
                continue;
            const parentSource = sourceByRevisionId.get(parent);
            if (parentSource !== undefined
                && parentSource !== input.revision.capture.sourceImage.sourceImageId) {
                invalid = true;
                continue;
            }
            if (ids.has(parent)) {
                parentById.set(input.revision.revisionId, parent);
                childCount.set(parent, (childCount.get(parent) ?? 0) + 1);
            }
        }
        for (const id of ids) {
            const seen = new Set();
            let current = id;
            while (current !== undefined) {
                if (seen.has(current)) {
                    invalid = true;
                    break;
                }
                seen.add(current);
                current = parentById.get(current);
            }
            if (invalid)
                break;
        }
        const terminals = sourceInputs.filter((input) => (childCount.get(input.revision.revisionId) ?? 0) === 0);
        if (invalid || terminals.length !== 1) {
            for (const input of sourceInputs)
                unassigned.add(input.revision.revisionId);
        }
        else {
            selected.push(terminals[0]);
        }
    }
    selected.sort((a, b) => compareText(a.revision.revisionId, b.revision.revisionId));
    return { selected, unassigned };
}
/** Normalize a machine-decoded item name into its unavoidable OCR-equivalence key. */
export function normalizeFredrickItemKey(itemName) {
    const normalized = itemName.normalize('NFKC').trim().replace(/\s+/gu, ' ');
    let key = '';
    for (const character of normalized) {
        if (character === 'I' || character === 'l')
            key += 'l';
        else if (character === 'i')
            key += 'i';
        else if (character === 'L')
            key += 'L';
        else
            key += character.toLowerCase();
    }
    return key;
}
function eligibleInput(input, semantics) {
    const transcription = input.revision.transcription;
    const { itemName, page, totalPages, totalResults, listings } = transcription;
    if (itemName === null || itemName.length === 0)
        return null;
    if (page === null || totalPages === null || totalResults === null)
        return null;
    if (!Number.isSafeInteger(totalResults) || totalResults < 0)
        return null;
    const expectedPages = expectedOwlPageCount(totalResults);
    if (totalPages !== expectedPages || page < 1 || page > expectedPages)
        return null;
    const expectedRows = expectedOwlRowsOnPage(totalResults, page);
    if (listings.length !== expectedRows)
        return null;
    const rows = new Set();
    for (const listing of listings) {
        if (!Number.isSafeInteger(listing.rowIndex)
            || listing.rowIndex < 0
            || listing.rowIndex >= expectedRows
            || rows.has(listing.rowIndex))
            return null;
        rows.add(listing.rowIndex);
        if (listing.sellerName === null || listing.sellerName.length === 0)
            return null;
        if (!Number.isSafeInteger(listing.bundle) || listing.bundle < 1)
            return null;
        if (!Number.isSafeInteger(listing.price) || listing.price < 0)
            return null;
        if (!Number.isSafeInteger(listing.quantity) || listing.quantity < 0)
            return null;
        if (semantics.materializeListing(listing) === null)
            return null;
    }
    for (let rowIndex = 0; rowIndex < expectedRows; rowIndex += 1) {
        if (!rows.has(rowIndex))
            return null;
    }
    const itemKey = normalizeFredrickItemKey(itemName);
    if (itemKey.length === 0)
        return null;
    const sourceImageId = input.revision.capture.sourceImage.sourceImageId;
    return {
        ...input,
        sourceImageId,
        evidenceImageId: input.revision.evidence.evidenceImageId,
        itemName,
        itemKey,
        page,
        totalPages,
        totalResults,
        compatibilityKey: JSON.stringify([semantics.variant, itemKey, totalResults]),
    };
}
export function resolveFredrickMarketSources(inputs, semantics = mapleLegendsMarketSemantics) {
    const preferred = selectPreferredInputs(inputs);
    const selectedBySource = new Map();
    const eligibleBySource = new Map();
    for (const selected of preferred.selected) {
        const sourceImageId = selected.revision.capture.sourceImage.sourceImageId;
        selectedBySource.set(sourceImageId, selected);
        const eligible = eligibleInput(selected, semantics);
        if (eligible === null)
            preferred.unassigned.add(selected.revision.revisionId);
        else
            eligibleBySource.set(sourceImageId, eligible);
    }
    return {
        eligibleBySource,
        selectedBySource,
        unassignedRevisionIds: sortedStrings(preferred.unassigned),
    };
}
function isStrictSuperset(child, parent) {
    if (child.length <= parent.length)
        return false;
    const childSet = new Set(child);
    return parent.every((value) => childSet.has(value));
}
function resolveClaimRecords(claim, eligibleBySource) {
    const recordsByPage = new Map();
    const pageToEvidence = new Map();
    let compatibility;
    for (const sourceImageId of claim.sourceImageIds) {
        const record = eligibleBySource.get(sourceImageId);
        if (record === undefined) {
            return {
                records: [...recordsByPage.values()],
                pageToEvidence,
                applicable: false,
                reason: 'source-not-market-eligible',
            };
        }
        if (compatibility === undefined)
            compatibility = record.compatibilityKey;
        else if (compatibility !== record.compatibilityKey) {
            return {
                records: [...recordsByPage.values()],
                pageToEvidence,
                applicable: false,
                reason: 'incompatible-sources',
            };
        }
        const existingEvidence = pageToEvidence.get(record.page);
        if (existingEvidence !== undefined && existingEvidence !== record.evidenceImageId) {
            return {
                records: [...recordsByPage.values()],
                pageToEvidence,
                applicable: false,
                reason: 'duplicate-page',
            };
        }
        const existingRecord = recordsByPage.get(record.page);
        if (existingRecord === undefined || compareCanonicalPageRecord(record, existingRecord) < 0) {
            recordsByPage.set(record.page, record);
            pageToEvidence.set(record.page, record.evidenceImageId);
        }
    }
    const records = [...recordsByPage.values()];
    records.sort((a, b) => compareNumber(a.page, b.page) || compareText(a.sourceImageId, b.sourceImageId));
    return { records, pageToEvidence, applicable: true };
}
function analyzeClaims(claims, eligibleBySource) {
    const uniqueClaims = new Map();
    const duplicateConflictIds = new Set();
    for (const claim of claims) {
        const current = uniqueClaims.get(claim.claimId);
        if (current === undefined)
            uniqueClaims.set(claim.claimId, claim);
        else if (JSON.stringify(current) !== JSON.stringify(claim))
            duplicateConflictIds.add(claim.claimId);
    }
    const preliminary = new Map();
    for (const claim of [...uniqueClaims.values()].sort((a, b) => compareText(a.claimId, b.claimId))) {
        let structurallyValid = !duplicateConflictIds.has(claim.claimId)
            && fredrickSearchClaimIsCanonical(claim);
        let reason;
        if (!structurallyValid)
            reason = 'invalid-claim';
        if (structurallyValid && claim.parentClaimId === undefined && claim.searchId !== claim.claimId) {
            structurallyValid = false;
            reason = 'invalid-root';
        }
        const resolved = resolveClaimRecords(claim, eligibleBySource);
        preliminary.set(claim.claimId, {
            claim,
            structurallyValid,
            applicable: structurallyValid && resolved.applicable,
            ...(reason ?? resolved.reason ? { reason: reason ?? resolved.reason } : {}),
            records: resolved.records,
            pageToEvidence: resolved.pageToEvidence,
        });
    }
    // Validate continuation relationships after every claim is indexed.
    for (const [claimId, node] of [...preliminary]) {
        if (!node.structurallyValid || node.claim.parentClaimId === undefined)
            continue;
        const parent = preliminary.get(node.claim.parentClaimId);
        let reason;
        if (parent === undefined
            || !parent.structurallyValid
            || parent.claim.repositoryId !== node.claim.repositoryId
            || parent.claim.searchId !== node.claim.searchId) {
            reason = 'invalid-parent';
        }
        else if (!isStrictSuperset(node.claim.sourceImageIds, parent.claim.sourceImageIds)) {
            reason = 'not-strict-superset';
        }
        else if (node.applicable && parent.applicable) {
            for (const [page, evidenceImageId] of parent.pageToEvidence) {
                if (node.pageToEvidence.get(page) !== evidenceImageId) {
                    reason = 'changed-parent-page';
                    break;
                }
            }
        }
        if (reason !== undefined) {
            preliminary.set(claimId, {
                ...node,
                structurallyValid: reason === 'changed-parent-page' ? node.structurallyValid : false,
                applicable: false,
                reason,
            });
        }
    }
    const roots = [...preliminary.values()].filter((node) => node.structurallyValid && node.claim.parentClaimId === undefined);
    // A source may occur in many snapshots of one lineage, but it must not be
    // claimed by two distinct roots in the same repository. Check every
    // structurally valid snapshot so a continuation cannot silently borrow a
    // source that is already owned by another lineage.
    const sourceOwners = new Map();
    for (const node of preliminary.values()) {
        if (!node.structurallyValid)
            continue;
        for (const sourceImageId of node.claim.sourceImageIds) {
            const key = JSON.stringify([node.claim.repositoryId, sourceImageId]);
            const owners = sourceOwners.get(key) ?? new Set();
            owners.add(node.claim.searchId);
            sourceOwners.set(key, owners);
        }
    }
    const ownershipConflictSearches = new Set();
    for (const owners of sourceOwners.values()) {
        if (owners.size > 1)
            for (const searchId of owners)
                ownershipConflictSearches.add(searchId);
    }
    const childrenByParent = new Map();
    for (const node of preliminary.values()) {
        const parent = node.claim.parentClaimId;
        if (parent === undefined)
            continue;
        const children = childrenByParent.get(parent) ?? [];
        children.push(node);
        childrenByParent.set(parent, children);
    }
    for (const children of childrenByParent.values()) {
        children.sort((a, b) => compareText(a.claim.claimId, b.claim.claimId));
    }
    const accepted = [];
    const branches = [];
    const acceptedClaimIds = new Set();
    const acceptedAncestorIds = new Set();
    const branchClaimIds = new Set();
    const ambiguousSourceIds = new Set();
    for (const root of roots.sort((a, b) => compareText(a.claim.claimId, b.claim.claimId))) {
        if (ownershipConflictSearches.has(root.claim.searchId) || !root.applicable)
            continue;
        let current = root;
        while (true) {
            const applicableChildren = (childrenByParent.get(current.claim.claimId) ?? [])
                .filter((child) => child.structurallyValid && child.applicable);
            if (applicableChildren.length === 0)
                break;
            if (applicableChildren.length > 1) {
                const childClaimIds = applicableChildren.map((child) => child.claim.claimId).sort(compareText);
                branches.push({
                    id: JSON.stringify(['lineage-branch-v1', current.claim.repositoryId, current.claim.searchId, current.claim.claimId]),
                    repositoryId: current.claim.repositoryId,
                    searchId: current.claim.searchId,
                    parentClaimId: current.claim.claimId,
                    childClaimIds,
                });
                for (const child of applicableChildren) {
                    branchClaimIds.add(child.claim.claimId);
                    for (const sourceImageId of child.claim.sourceImageIds)
                        ambiguousSourceIds.add(sourceImageId);
                }
                break;
            }
            acceptedAncestorIds.add(current.claim.claimId);
            current = applicableChildren[0];
        }
        acceptedClaimIds.add(current.claim.claimId);
        accepted.push({
            repositoryId: current.claim.repositoryId,
            searchId: current.claim.searchId,
            head: current,
        });
    }
    for (const node of preliminary.values()) {
        if (ownershipConflictSearches.has(node.claim.searchId)) {
            for (const sourceImageId of node.claim.sourceImageIds)
                ambiguousSourceIds.add(sourceImageId);
        }
    }
    const diagnostics = [];
    for (const node of [...preliminary.values()].sort((a, b) => compareText(a.claim.claimId, b.claim.claimId))) {
        let status;
        let reason = node.reason;
        if (ownershipConflictSearches.has(node.claim.searchId)) {
            status = 'source-ownership-conflict';
            reason = 'source-owned-by-multiple-lineages';
        }
        else if (!node.structurallyValid) {
            status = 'invalid-structure';
        }
        else if (!node.applicable) {
            status = 'inapplicable';
        }
        else if (branchClaimIds.has(node.claim.claimId)) {
            status = 'ambiguous-branch';
            reason = 'multiple-applicable-children';
        }
        else if (acceptedClaimIds.has(node.claim.claimId)) {
            status = 'accepted-head';
        }
        else if (acceptedAncestorIds.has(node.claim.claimId)) {
            status = 'accepted-ancestor';
        }
        else {
            status = 'not-selected';
        }
        diagnostics.push({
            claimId: node.claim.claimId,
            repositoryId: node.claim.repositoryId,
            searchId: node.claim.searchId,
            status,
            ...(reason === undefined ? {} : { reason }),
        });
    }
    const sourceToLineages = new Map();
    const evidenceToLineages = new Map();
    for (const node of preliminary.values()) {
        if (!node.structurallyValid)
            continue;
        const lineageKey = JSON.stringify([node.claim.repositoryId, node.claim.searchId]);
        for (const sourceImageId of node.claim.sourceImageIds) {
            const lineages = sourceToLineages.get(sourceImageId) ?? new Set();
            lineages.add(lineageKey);
            sourceToLineages.set(sourceImageId, lineages);
        }
        for (const evidenceImageId of node.pageToEvidence.values()) {
            const lineages = evidenceToLineages.get(evidenceImageId) ?? new Set();
            lineages.add(lineageKey);
            evidenceToLineages.set(evidenceImageId, lineages);
        }
    }
    const acceptedBySearch = new Map();
    for (const lineage of accepted) {
        acceptedBySearch.set(JSON.stringify([lineage.repositoryId, lineage.searchId]), lineage);
    }
    return {
        nodesById: preliminary,
        accepted,
        acceptedBySearch,
        sourceToLineages,
        evidenceToLineages,
        diagnostics,
        branches: branches.sort((a, b) => compareText(a.id, b.id)),
        ambiguousSourceIds,
    };
}
function percentile(sorted, probability) {
    if (sorted.length === 0)
        throw new RangeError('Cannot calculate an empty percentile.');
    const h = (sorted.length - 1) * probability;
    const lower = Math.floor(h);
    const upper = Math.ceil(h);
    return sorted[lower] + (h - lower) * (sorted[upper] - sorted[lower]);
}
export function calculateFredrickPriceStatistics(values) {
    if (values.length === 0)
        return null;
    const sorted = [...values].sort(compareNumber);
    const mean = sorted.reduce((sum, value) => sum + value, 0) / sorted.length;
    const variance = sorted.reduce((sum, value) => sum + ((value - mean) ** 2), 0) / sorted.length;
    return {
        count: sorted.length,
        min: sorted[0],
        p10: percentile(sorted, 0.1),
        p25: percentile(sorted, 0.25),
        median: percentile(sorted, 0.5),
        p75: percentile(sorted, 0.75),
        p90: percentile(sorted, 0.9),
        max: sorted[sorted.length - 1],
        mean,
        populationStandardDeviation: Math.sqrt(variance),
    };
}
function materializeAcceptedLineage(lineage, semantics) {
    const records = [...lineage.head.records].sort((a, b) => compareNumber(a.page, b.page) || compareText(a.revision.revisionId, b.revision.revisionId));
    const revisionIds = sortedStrings(records.map((record) => record.revision.revisionId));
    const sourceImageIds = sortedStrings(records.map((record) => record.sourceImageId));
    const presentPages = records.map((record) => record.page);
    const observedAtStart = Math.min(...records.map((record) => record.revision.capture.observedAt));
    const observedAtEnd = Math.max(...records.map((record) => record.revision.capture.observedAt));
    const representative = records[0];
    const baseSearch = {
        schemaVersion: FREDRICK_DERIVED_SCHEMA_VERSION,
        searchId: lineage.searchId,
        processorVersion: FREDRICK_MARKET_PROCESSOR_VERSION,
        marketVariant: semantics.variant,
        sourceImageIds,
        revisionIds,
        itemName: representative.itemName,
        itemKey: representative.itemKey,
        totalResults: representative.totalResults,
        observedAtStart,
        observedAtEnd,
        expectedPageCount: representative.totalPages,
        presentPages,
        completeness: 'partial',
        groupingState: 'high-confidence',
    };
    const search = {
        ...baseSearch,
        completeness: derivedSearchIsStructurallyComplete(baseSearch) ? 'complete' : 'partial',
    };
    const pages = [];
    const listings = [];
    let totalUnits = 0;
    for (const record of records) {
        pages.push({
            id: JSON.stringify(['search-page-v2', lineage.searchId, record.page, record.revision.revisionId]),
            searchId: lineage.searchId,
            sourceImageId: record.sourceImageId,
            revisionId: record.revision.revisionId,
            page: record.page,
        });
        for (const row of [...record.revision.transcription.listings]
            .sort((a, b) => compareNumber(a.rowIndex, b.rowIndex))) {
            const market = semantics.materializeListing(row);
            if (market === null)
                throw new TypeError('An eligible market row became unmaterializable.');
            const nextTotal = totalUnits + market.units;
            if (!Number.isSafeInteger(nextTotal))
                throw new RangeError('Total listing units exceed safe integer range.');
            totalUnits = nextTotal;
            listings.push({
                id: JSON.stringify(['listing-v2', lineage.searchId, record.page, row.rowIndex, record.revision.revisionId]),
                searchId: lineage.searchId,
                sourceImageId: record.sourceImageId,
                revisionId: record.revision.revisionId,
                page: record.page,
                itemName: record.itemName,
                itemKey: record.itemKey,
                observedAt: record.revision.capture.observedAt,
                marketVariant: semantics.variant,
                rowIndex: row.rowIndex,
                sellerName: row.sellerName,
                storeName: row.storeName,
                price: market.price,
                units: market.units,
                storeNameVerified: false,
            });
        }
    }
    const summary = {
        schemaVersion: FREDRICK_DERIVED_SCHEMA_VERSION,
        searchId: lineage.searchId,
        processorVersion: FREDRICK_MARKET_PROCESSOR_VERSION,
        statisticsVersion: FREDRICK_MARKET_STATISTICS_VERSION,
        marketVariant: semantics.variant,
        itemName: search.itemName,
        itemKey: search.itemKey,
        observedAt: observedAtStart,
        observedAtEnd,
        totalResults: search.totalResults,
        listingCount: listings.length,
        totalUnits,
        completeness: search.completeness,
        groupingState: 'high-confidence',
        prices: calculateFredrickPriceStatistics(listings.map((listing) => listing.price)),
    };
    return { search, pages, listings, summary };
}
function searchOrder(left, right) {
    return compareText(left.itemKey, right.itemKey)
        || compareNumber(left.observedAtStart, right.observedAtStart)
        || compareText(left.searchId, right.searchId);
}
function latestIsAfter(left, right) {
    return left.observedAt > right.observedAt
        || (left.observedAt === right.observedAt && left.observedAtEnd > right.observedAtEnd)
        || (left.observedAt === right.observedAt
            && left.observedAtEnd === right.observedAtEnd
            && compareText(left.searchId, right.searchId) > 0);
}
export function processFredrickMarketRevisions(inputs, claims = [], semantics = mapleLegendsMarketSemantics) {
    const resolved = resolveFredrickMarketSources(inputs, semantics);
    const analysis = analyzeClaims(claims, resolved.eligibleBySource);
    const searches = [];
    const pages = [];
    const listings = [];
    const summaries = [];
    const assignedSourceIds = new Set();
    for (const lineage of analysis.accepted) {
        const materialized = materializeAcceptedLineage(lineage, semantics);
        searches.push(materialized.search);
        pages.push(...materialized.pages);
        listings.push(...materialized.listings);
        summaries.push(materialized.summary);
        for (const sourceImageId of lineage.head.claim.sourceImageIds)
            assignedSourceIds.add(sourceImageId);
    }
    searches.sort(searchOrder);
    summaries.sort((a, b) => searchOrder({ itemKey: a.itemKey, observedAtStart: a.observedAt, searchId: a.searchId }, { itemKey: b.itemKey, observedAtStart: b.observedAt, searchId: b.searchId }));
    pages.sort((a, b) => compareText(a.searchId, b.searchId)
        || compareNumber(a.page, b.page)
        || compareText(a.revisionId, b.revisionId));
    listings.sort((a, b) => compareText(a.searchId, b.searchId)
        || compareNumber(a.page, b.page)
        || compareNumber(a.rowIndex, b.rowIndex)
        || compareText(a.revisionId, b.revisionId));
    const latestByItem = new Map();
    for (const summary of summaries) {
        if (summary.completeness !== 'complete')
            continue;
        const current = latestByItem.get(summary.itemKey);
        if (current === undefined || latestIsAfter(summary, current))
            latestByItem.set(summary.itemKey, summary);
    }
    const latestItems = [...latestByItem.values()]
        .map((summary) => ({
        ...summary,
        completeness: 'complete',
        groupingState: 'high-confidence',
    }))
        .sort((a, b) => compareText(a.itemKey, b.itemKey));
    const unassigned = new Set(resolved.unassignedRevisionIds);
    const ambiguous = new Set();
    for (const [sourceImageId, selected] of resolved.selectedBySource) {
        if (analysis.ambiguousSourceIds.has(sourceImageId))
            ambiguous.add(selected.revision.revisionId);
        else if (!assignedSourceIds.has(sourceImageId))
            unassigned.add(selected.revision.revisionId);
    }
    return {
        searches,
        pages,
        listings,
        summaries,
        latestItems,
        claimDiagnostics: analysis.diagnostics,
        lineageBranches: analysis.branches,
        unassignedRevisionIds: sortedStrings(unassigned),
        ambiguousRevisionIds: sortedStrings(ambiguous),
    };
}
function emitSessionGroups(sourceImageIds, eligibleBySource) {
    const bucketOrder = [];
    const buckets = new Map();
    const seenByBucket = new Map();
    for (const sourceImageId of sourceImageIds) {
        const record = eligibleBySource.get(sourceImageId);
        if (record === undefined)
            continue;
        if (!buckets.has(record.compatibilityKey)) {
            buckets.set(record.compatibilityKey, []);
            seenByBucket.set(record.compatibilityKey, new Set());
            bucketOrder.push(record.compatibilityKey);
        }
        const seen = seenByBucket.get(record.compatibilityKey);
        if (seen.has(sourceImageId))
            continue;
        seen.add(sourceImageId);
        buckets.get(record.compatibilityKey).push(sourceImageId);
    }
    const result = [];
    for (const key of bucketOrder) {
        const currentSources = [];
        const currentRecords = [];
        let pageEvidence = new Map();
        for (const sourceImageId of buckets.get(key)) {
            const record = eligibleBySource.get(sourceImageId);
            const existingEvidence = pageEvidence.get(record.page);
            if (existingEvidence === record.evidenceImageId) {
                continue;
            }
            if (currentSources.length > 0 && existingEvidence !== undefined) {
                result.push({ sourceImageIds: [...currentSources], records: [...currentRecords] });
                currentSources.length = 0;
                currentRecords.length = 0;
                pageEvidence = new Map();
            }
            currentSources.push(sourceImageId);
            currentRecords.push(record);
            pageEvidence.set(record.page, record.evidenceImageId);
        }
        if (currentSources.length > 0) {
            result.push({ sourceImageIds: [...currentSources], records: [...currentRecords] });
        }
    }
    return result;
}
export function planFredrickImportSession(repositoryId, sessionSourceImageIds, inputs, claims, semantics = mapleLegendsMarketSemantics) {
    if (!repositoryId)
        throw new TypeError('repositoryId must be non-empty.');
    const resolved = resolveFredrickMarketSources(inputs, semantics);
    const analysis = analyzeClaims(claims, resolved.eligibleBySource);
    const proposals = [];
    const unassignedSeen = new Set();
    for (const sourceImageId of sessionSourceImageIds) {
        if (resolved.eligibleBySource.has(sourceImageId) || unassignedSeen.has(sourceImageId))
            continue;
        unassignedSeen.add(sourceImageId);
        proposals.push({
            proposalIndex: proposals.length,
            status: 'unassigned-page',
            sourceImageIds: [sourceImageId],
            itemName: null,
            presentPages: [],
            expectedPageCount: null,
        });
    }
    for (const group of emitSessionGroups(sessionSourceImageIds, resolved.eligibleBySource)) {
        const sourceImageIds = sortedStrings(group.sourceImageIds);
        const presentPages = [...new Set(group.records.map((record) => record.page))].sort(compareNumber);
        const representative = [...group.records].sort((a, b) => compareNumber(a.page, b.page))[0];
        const lineageKeys = new Set();
        for (const record of group.records) {
            const sourceImageId = record.sourceImageId;
            for (const lineageKey of analysis.sourceToLineages.get(sourceImageId) ?? []) {
                const [ownerRepositoryId] = JSON.parse(lineageKey);
                if (ownerRepositoryId === repositoryId)
                    lineageKeys.add(lineageKey);
            }
            for (const lineageKey of analysis.evidenceToLineages.get(record.evidenceImageId) ?? []) {
                const [ownerRepositoryId] = JSON.parse(lineageKey);
                if (ownerRepositoryId === repositoryId)
                    lineageKeys.add(lineageKey);
            }
        }
        if (lineageKeys.size > 1) {
            proposals.push({
                proposalIndex: proposals.length,
                status: 'ambiguous-lineage',
                sourceImageIds,
                itemName: representative.itemName,
                presentPages,
                expectedPageCount: representative.totalPages,
            });
            continue;
        }
        if (lineageKeys.size === 0) {
            const claim = createFredrickRootSearchClaimV1({ repositoryId, sourceImageIds });
            proposals.push({
                proposalIndex: proposals.length,
                status: 'new-search',
                sourceImageIds,
                itemName: representative.itemName,
                presentPages,
                expectedPageCount: representative.totalPages,
                searchId: claim.searchId,
                claim,
            });
            continue;
        }
        const lineageKey = [...lineageKeys][0];
        const accepted = analysis.acceptedBySearch.get(lineageKey);
        if (accepted === undefined) {
            proposals.push({
                proposalIndex: proposals.length,
                status: 'ambiguous-lineage',
                sourceImageIds,
                itemName: representative.itemName,
                presentPages,
                expectedPageCount: representative.totalPages,
            });
            continue;
        }
        const parentSources = new Set(accepted.head.claim.sourceImageIds);
        const unionSources = new Set(accepted.head.claim.sourceImageIds);
        let conflict = false;
        for (const record of group.records) {
            const occupiedEvidence = accepted.head.pageToEvidence.get(record.page);
            if (occupiedEvidence !== undefined && occupiedEvidence !== record.evidenceImageId) {
                conflict = true;
                break;
            }
            if (occupiedEvidence === undefined)
                unionSources.add(record.sourceImageId);
        }
        if (conflict) {
            proposals.push({
                proposalIndex: proposals.length,
                status: 'conflicting-continuation',
                sourceImageIds,
                itemName: representative.itemName,
                presentPages,
                expectedPageCount: representative.totalPages,
                searchId: accepted.searchId,
                parentClaimId: accepted.head.claim.claimId,
            });
            continue;
        }
        if (unionSources.size === parentSources.size) {
            proposals.push({
                proposalIndex: proposals.length,
                status: 'already-covered',
                sourceImageIds,
                itemName: representative.itemName,
                presentPages,
                expectedPageCount: representative.totalPages,
                searchId: accepted.searchId,
                parentClaimId: accepted.head.claim.claimId,
            });
            continue;
        }
        const claim = createFredrickContinuationSearchClaimV1({
            repositoryId,
            searchId: accepted.head.claim.searchId,
            parentClaimId: accepted.head.claim.claimId,
            sourceImageIds: sortedStrings(unionSources),
        });
        proposals.push({
            proposalIndex: proposals.length,
            status: 'extended-existing-search',
            sourceImageIds,
            itemName: representative.itemName,
            presentPages,
            expectedPageCount: representative.totalPages,
            searchId: claim.searchId,
            parentClaimId: claim.parentClaimId,
            claim,
        });
    }
    const snapshotAcceptedHeadBySearch = new Map();
    for (const accepted of analysis.accepted) {
        snapshotAcceptedHeadBySearch.set(JSON.stringify([accepted.repositoryId, accepted.searchId]), accepted.head.claim.claimId);
    }
    return { proposals, snapshotAcceptedHeadBySearch };
}
export const fredrickMarketProcessor = {
    version: FREDRICK_MARKET_PROCESSOR_VERSION,
    statisticsVersion: FREDRICK_MARKET_STATISTICS_VERSION,
    marketVariant: mapleLegendsMarketSemantics.variant,
    process: processFredrickMarketRevisions,
};
